import type { FuturesBar } from "@/types/futuresChart"

export type ChartInterval = "daily" | "weekly"
export type ChartRange = "1m" | "1y" | "5y" | "ytd" | "all"

export const DEFAULT_CHART_INTERVAL: ChartInterval = "daily"
export const DEFAULT_CHART_RANGE: ChartRange = "1y"

export const CHART_INTERVALS: { id: ChartInterval; label: string }[] = [
  { id: "daily", label: "Daily" },
  { id: "weekly", label: "Weekly" },
]

export const CHART_RANGES: { id: ChartRange; label: string }[] = [
  { id: "1m", label: "1M" },
  { id: "1y", label: "1Y" },
  { id: "5y", label: "5Y" },
  { id: "ytd", label: "YTD" },
  { id: "all", label: "All" },
]

const ZOOM_PIXEL_SENSITIVITY = 0.0007
const MAX_ZOOM_STEP = 0.06

export function parseUtcDate(time: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}/.test(time)) return null
  const date = new Date(`${time.slice(0, 10)}T00:00:00Z`)
  return Number.isNaN(date.getTime()) ? null : date
}

export function isCalendarTimeline(times: string[]): boolean {
  return times.length > 0 && times.every((time) => parseUtcDate(time) !== null)
}

export function isoWeekStart(time: string): string {
  const date = parseUtcDate(time)
  if (!date) return time.slice(0, 10)
  const day = date.getUTCDay()
  const offset = day === 0 ? -6 : 1 - day
  date.setUTCDate(date.getUTCDate() + offset)
  return date.toISOString().slice(0, 10)
}

export function aggregateWeeklyBars(bars: FuturesBar[]): FuturesBar[] {
  if (bars.length === 0) return []

  const groups = new Map<string, FuturesBar[]>()
  for (const bar of bars) {
    const key = isoWeekStart(bar.time)
    const group = groups.get(key)
    if (group) group.push(bar)
    else groups.set(key, [bar])
  }

  return [...groups.values()].map((group) => {
    const first = group[0]
    const last = group[group.length - 1]
    return {
      id: last.id,
      time: last.time,
      open: first.open,
      high: Math.max(...group.map((bar) => bar.high)),
      low: Math.min(...group.map((bar) => bar.low)),
      close: last.close,
      last: last.last ?? last.close,
      volume: sumNullable(group.map((bar) => bar.volume)),
      open_interest: last.open_interest,
      ...(last.provisional
        ? { provisional: true, as_of: last.as_of, delay_minutes: last.delay_minutes }
        : {}),
    }
  })
}

export function visibleBarCount(bars: { time: string }[], range: ChartRange): number {
  if (bars.length === 0) return 0
  if (range === "all") return bars.length

  const last = bars[bars.length - 1]
  const endDate = parseUtcDate(last.time)
  if (!endDate) return bars.length
  const startTime = rangeStartTime(endDate, range)
  if (!startTime) return bars.length
  const startIndex = bars.findIndex((bar) => bar.time >= startTime)
  if (startIndex === -1) return bars.length
  return bars.length - startIndex
}

export function zoomedBarCount(
  currentCount: number,
  deltaY: number,
  options: { minWindow: number; length: number; deltaMode?: number },
): number {
  const { minWindow, length, deltaMode = 0 } = options
  const pixels = normalizedDeltaY(deltaY, deltaMode)
  if (pixels === 0 || length === 0) return Math.min(length, Math.max(minWindow, currentCount))

  const step = Math.min(MAX_ZOOM_STEP, Math.abs(pixels) * ZOOM_PIXEL_SENSITIVITY)
  if (step === 0) return Math.min(length, Math.max(minWindow, currentCount))

  const factor = pixels > 0 ? 1 + step : 1 - step
  let next = Math.round(currentCount * factor)
  if (next === currentCount) next = pixels > 0 ? currentCount + 1 : currentCount - 1
  return Math.min(length, Math.max(minWindow, next))
}

function rangeStartTime(endDate: Date, range: Exclude<ChartRange, "all">): string | null {
  const start = new Date(endDate.getTime())
  switch (range) {
    case "1m":
      start.setUTCMonth(start.getUTCMonth() - 1)
      break
    case "1y":
      start.setUTCFullYear(start.getUTCFullYear() - 1)
      break
    case "5y":
      start.setUTCFullYear(start.getUTCFullYear() - 5)
      break
    case "ytd":
      return new Date(Date.UTC(endDate.getUTCFullYear(), 0, 1)).toISOString().slice(0, 10)
  }
  return Number.isNaN(start.getTime()) ? null : start.toISOString().slice(0, 10)
}

function normalizedDeltaY(deltaY: number, deltaMode: number): number {
  if (deltaMode === 1) return deltaY * 16
  if (deltaMode === 2) return deltaY * 400
  return deltaY
}

function sumNullable(values: Array<number | null>): number | null {
  const present = values.filter((value): value is number => value != null)
  return present.length === 0 ? null : present.reduce((sum, value) => sum + value, 0)
}
